/**
  * @class meleActorDopplerMeta
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-dopplermeta>` custom web element for showing doppler radar metadata
  */

import meleActor from 'meleActor'

const PROPERTIES = _.extend({}, {
  shortcuts: {
    description: 'shortcuts for speed limits',
    type: 'array',
    items: {
      type: 'object',
      properties: {
        direction_limit: { type: 'string', enum: ['approaching', 'receding', 'both'], description: 'direction of measured vehicle' },
        lane_limit: { type: 'integer', description: 'lane of measured vehicle' },
        speed_limit: { type: 'integer', description: 'speed limit set in radar' },
        hit_tolerance: { type: 'integer', description: 'triggering speed limit' },
      },
    },
    default: [
      { hit_tolerance: 10, speed_limit: 50, direction_limit: 'both', lane_limit: 12 },
      { hit_tolerance: 10, speed_limit: 90, direction_limit: 'both', lane_limit: 12 },
      { hit_tolerance: 10, speed_limit: 130, direction_limit: 'receding', lane_limit: 12 },
      { hit_tolerance: 10, speed_limit: 130, direction_limit: 'receding', lane_limit: 23 },
    ],
  },
  lane_zooms: {
    description: 'zooms for lanes',
    type: 'object',
    properties: {
      1: { type: 'number' },
      2: { type: 'number' },
      3: { type: 'number' },
      12: { type: 'number' },
      23: { type: 'number' },
      123: { type: 'number' },
    },
    default: {
      1: 0,
      12: 0,
      123: 0,
    },
  },
  simulator: {
    type: 'boolean',
    description: 'if true, simulator mode is available',
  },
}, meleActor._properties)

const SVG_ARROWS = require('../../res/icons/lanes.js')

export default class meleActorDopplerMeta extends meleActor {
  init() {
    this.errors = ['connection']

    this.info_elements = {}
    for(const key of ['sog_speed', 'mode', 'speed_limit', 'hit_limit', 'lane_limit', 'direction_limit', 'sn']) {
      const el = this.appendChildren({
        tag: 'div',
        key,
        class: 'indicator',
        children: [
          { tag: 'mele-text', label: 'doppler.'+key, class: 'title' },
        ],
      })
      if(key === 'mode' || key === 'direction_limit') {
        this.info_elements['el_'+key] = el.appendChildren({ tag: 'mele-text', class: 'subtitle', label: '-' })
      }else {
        this.info_elements['el_'+key] = el.appendChildren({ tag: 'span', class: 'subtitle', html: '-' })
      }
    }
    this.identity_element = this.appendChildren({
      tag: 'mele-text',
      class: 'identity',
    })

    this.startstop = this.appendChildren({
      tag: 'mele-button',
      key: 'startstop',
      type: 'toolbar',
      class: 'ctrl',
      iconleft: 'doppler.error',
    })
    this.startstop.onclick = this.startStop.bind(this)

    this.config = this.appendChildren({ tag: 'dopplerconfig', class: 'ctrl' })
    this.config.onclick = this.openConfiguration.bind(this)

    this.alerts = this.appendChildren({ tag: 'alerts' })

    this.info = this.appendChildren({
      tag: 'mele-view',
      key: 'calibration_info',
      items: [
        {
          tag: 'mele-text',
          label: 'doppler.NoCalibration',
        },
        {
          tag: 'div',
          children: [
            { key: 'calibration_dt', label: 'dopplercalibration.calibration_dt', tag: 'mele-input', type: 'datetime_us', readonly: true },
            { key: 'save_dt', label: 'dopplercalibration.save_dt', tag: 'mele-input', type: 'datetime_us', readonly: true },
            { key: 'mounting', label: 'dopplercalibration.mounting', tag: 'mele-input', type: 'string', readonly: true },
            { key: 'calibrator', label: 'dopplercalibration.calibrator', tag: 'mele-input', type: 'string', readonly: true },
            { key: 'remark', label: 'dopplercalibration.remark', tag: 'mele-input', type: 'string', readonly: true },
            { key: 'client_version', label: 'doppler.client_version', tag: 'mele-input', type: 'string', readonly: true },
          ],
        },
        {
          tag: 'div',
          children: [
            { key: 'radar', label: 'doppler.radar', tag: 'mele-input', type: 'string', readonly: true },
            { key: 'radar_height', label: 'doppler.radar_height', tag: 'mele-input', type: 'string', after: 'm', readonly: true },
            { key: 'radar_hv_angle', label: 'doppler.radar_hv_angle', tag: 'mele-input', type: 'string', after: '°', readonly: true },
            { key: 'radar_yaw', label: 'doppler.radar_yaw', tag: 'mele-input', type: 'string', after: '°', readonly: true },
            { key: 'radar_lat', label: 'doppler.radar_lat', tag: 'mele-input', type: 'string', after: 'm', readonly: true },
            { key: 'radar_lon', label: 'doppler.radar_lon', tag: 'mele-input', type: 'string', after: 'm', readonly: true },
            { key: 'client_checksum', label: 'doppler.client_checksum', tag: 'mele-input', type: 'string', readonly: true },
          ],
        },
        {
          tag: 'div',
          children: [
            { key: 'camera_lens', label: 'doppler.camera_lens', tag: 'mele-input', type: 'string', readonly: true },
            { key: 'lens', label: 'doppler.lens', tag: 'mele-input', type: 'string', readonly: true },
            { key: 'camera_height', label: 'doppler.camera_height', tag: 'mele-input', type: 'string', after: 'm', readonly: true },
            { key: 'hv_angle', label: 'doppler.hv_angle', tag: 'mele-input', type: 'string', after: '°', readonly: true },
            { key: 'camera_yaw_pitch_roll', label: 'doppler.camera_yaw_pitch_roll', tag: 'mele-input', type: 'string', after: '°', readonly: true },
            { tag: 'mele-input', type: 'invisible' },
            { key: 'version', label: 'doppler.version', tag: 'mele-input', type: 'string', readonly: true },
          ],
        },
        {
          tag: 'div',
          children: [
            { key: 'resolution', label: 'doppler.resolution', tag: 'mele-input', type: 'string', after: 'px', readonly: true },
            { key: 'zoom', label: 'doppler.zoom', tag: 'mele-input', type: 'string', after: 'x', readonly: true },
            { tag: 'mele-input', type: 'invisible' },
            { tag: 'mele-input', type: 'invisible' },
            { key: 'sn', label: 'doppler.sn', tag: 'mele-input', type: 'string', readonly: true },
            { key: 'checksum', label: 'doppler.checksum', tag: 'mele-input', type: 'string', readonly: true },
          ],
        },
      ],
    })

    this.listener = this.closest('.mele-player').listener

    super.init()
    //this.updateStatus()
  } // init

  message({ action, params }) {
    if(action==='mmDopplerMeta' && params.channel===parseInt(this.parentElement.ch)) {
      if(params.limits?.mode === 'simulator') {
        this.closest('mele-player').setAttribute('simulator', '1')
      }else {
        this.closest('mele-player').removeAttribute('simulator')
      }
      this.updateStatus(params)
      this.updateCalibrationInfo()
    }
  } // message

  toggleCalibration() {
    const on = this.getAttribute('showcalibration')
    if(on) {
      this.removeAttribute('showcalibration')
    }else {
      this.setAttribute('showcalibration', '1')
    }
    this.updateCalibrationInfo()
  } // toggleCalibration

  startStop() {
    const channel = parseInt(this.parentElement.ch)
    const state = this.getAttribute('state')
    if(state==='off' || state==='poweroff') {
      this.listener.publish('SERVER@#DOPPLER', 'mmDopplerStart', { channel })
    }else {
      this.listener.publish('SERVER@#DOPPLER', 'mmDopplerStop', { channel })
    }
  } // startStop

  stop() {
    const channel = parseInt(this.parentElement.ch)
    this.listener.publish('SERVER@#DOPPLER', 'mmDopplerStop', { channel })
  } // stop

  updateErrors() {
    const errors = this.errors || ['connection']
    let alerts = errors.map(e=>`<div>${this.translate(`doppler.error.${e}`)}</div>`).join('')
    if(!alerts) {
      const state = this.meta?.state || 'error'
      alerts = `<div>${this.translate(`doppler.doppler_state.${state}`)}</div>`
      if(state === 'on' || state === 'off' || state === 'poweroff') {
        this.alerts.classList.add('info')
      }
    }else {
      this.alerts.classList.remove('info')
    }
    if(this.alerts.innerHTML !== alerts) this.alerts.innerHTML = alerts
  } // updateErrors

  languageChanged() {
    this.updateErrors()
  } // languageChanged

  stateChanged(value) {
    if(value==='connecting') {
      // simulate empty meta to indicate when client diconnects from mDoppler
      // this.updateStatus()
    }
  } // stateChanged

  connected(params) {
    this.last_disconnect_reason = ''
  } // disconnected

  disconnected(params) {
    this.last_disconnect_reason = params?.reason || ''
  } // disconnected

  updateStatus(meta) {
    const { state, identity, sog, limits } = meta || {}
    const errors = !meta ? ['connection'] : Array.isArray(meta?.errors) ? meta.errors : []
    this.errors = errors
    const { direction_limit, lane_limit, speed_limit, hit_limit } = limits || {}
    const hit_tolerance = hit_limit - speed_limit
    this.meta = meta
    if(this.status_timeout) {
      clearTimeout(this.status_timeout)
      delete this.status_timeout
    }

    const { el_sog_speed, el_mode, el_speed_limit, el_hit_limit, el_lane_limit, el_direction_limit, el_sn, el_version, el_checksum } = this.info_elements
    const sog_km_h = sog?.sog_speed * 3.6 // convert m/s to km/h
    el_sog_speed.innerHTML = isNaN(sog_km_h) ? '-' : `${sog_km_h.toFixed(1)} km/h`
    el_mode.label = limits?.mode ? `doppler.mode.${limits.mode}` : '-'
    el_speed_limit.innerHTML = speed_limit > 0 ? `${speed_limit} km/h` : '-'
    el_hit_limit.innerHTML = hit_limit > 0 ? `${hit_limit} km/h` : '-'
    el_lane_limit.innerHTML = isNaN(lane_limit) ? '-' : lane_limit.toString().split('').join('/')
    el_direction_limit.label = direction_limit ? `doppler.direction_limit.${direction_limit}` : '-'
    el_sn.innerHTML = identity?.sn || '-'

    const version_parts = String(identity?.version??'').split('/')
    const version = version_parts[4]||'-'
    this.identity_element.label = `{{doppler.version}}: ${version || '-'} {{doppler.checksum}}: ${identity?.checksum || '-'}`

    if(errors.includes('connection') && /MD5/.test(this.last_disconnect_reason)) errors.push('client_checksum')
    let alerts = errors.map(e=>`<div>${this.translate(`doppler.error.${e}`)}</div>`).join('')
    if(!alerts) {
      alerts = `<div>${this.translate(`doppler.doppler_state.${state}`)}</div>`
      if(state === 'on' || state === 'off' || state === 'poweroff') {
        this.alerts.classList.add('info')
      }
    }else {
      this.alerts.classList.remove('info')
    }
    if(this.alerts.innerHTML !== alerts) this.alerts.innerHTML = alerts

    const uncalibrated = errors.includes('connection') || errors.includes('no_calibration')

    this.setAttribute('state', state ?? 'error')

    const mode = limits?.mode
    let mode_icon = ''
    if(mode==='dry') {
      mode_icon = `
        <svg viewBox="0 0 100 100">
          <circle class="blinkbg" cx="50" cy="50" r="50" fill="var(--config-fill)"></circle>
          <circle cx="50" cy="50" r="40" fill="white"></circle>
          <path d="M15,73 l58,-58 M18,76 l58,-58 M21,79 l58,-58 M24,82 l58,-58 M27,85 l58,-58" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      `
    }else if(mode==='simulator') {
      mode_icon = `
        <svg viewBox="0 0 100 100">
          <circle class="blinkbg" cx="50" cy="50" r="50" fill="var(--config-fill)"></circle>
          <circle cx="50" cy="50" r="40" fill="white"></circle>
          <text x="50" y="48" text-anchor="middle" dominant-baseline="central" fill="black" stroke="black" stroke-width="1" font-family="Verdana, sans-serif" font-size="60">S</text>
        </svg>
      `
    }else if(mode) {
      const arrows = SVG_ARROWS[direction_limit+lane_limit] || ''
      let one = ''
      if(mode === 'manual') {
        one = '<text x="74" y="28" text-anchor="middle" dominant-baseline="middle" fill="var(--red)" stroke="var(--red)" stroke-width="2" font-family="Verdana, sans-serif" font-size="18">1</text>'
      }
      mode_icon = `
        <svg viewBox="0 0 100 100">
          <circle class="blinkbg" cx="50" cy="50" r="50" fill="var(--config-fill)" />
          <circle cx="50" cy="50" r="40" fill="white" />
          <text x="50" y="50" text-anchor="middle" dominant-baseline="middle" fill="black" stroke="black" stroke-width="1" font-family="Verdana, sans-serif" font-size="32">${speed_limit || ''}</text>
          <text x="50" y="75" text-anchor="middle" dominant-baseline="middle" fill="black" stroke="black" stroke-width="1" font-family="Verdana, sans-serif" font-size="20">${hit_tolerance >= 0 ? '+' + hit_tolerance : ''}</text>
          <g transform="scale(0.75) translate(15,6)">
            <path d="${SVG_ARROWS[123]}" fill="none" stroke="#000000aa" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="5,5"/>
            <path d="${arrows}" fill="none" stroke="#000000" stroke-width="3" stroke-linejoin="round" />
          </g>
          ${one}
        </svg>
      `
    }

    if(this.config.innerHTML !== mode_icon) {
      this.config.innerHTML = mode_icon
    }

    this.startstop.iconleft = state==='on' ? 'doppler.stop' : state==='off' || state==='poweroff' ? 'doppler.start' : 'doppler.error'
    this.startstop.disabled = !(state==='on' || state==='off' || state==='poweroff')

    this.status_timeout = setTimeout(()=>{
      this.updateStatus()
    }, 2000)

    this.updateModalState(true)
  } // updateStatus

  openConfiguration() {
    const state = this.getAttribute('state')
    if(state!=='off') {
      this.stop()
    }else if(this.errors && this.errors.includes('connection')) {
      return
    }
    let elModal = document.body.find('@dopplermeta_status')
    if(!elModal) {
      elModal = this.modalOpen(require('./meleActorDopplerMeta_status.json'))

      // create shortcut buttons
      let html = ''
      for(const shortcut of this.shortcuts) {
        let { direction_limit, lane_limit, speed_limit, hit_tolerance } = shortcut
        direction_limit = direction_limit || ''
        lane_limit = lane_limit>=1 ? lane_limit : ''
        const arrows = SVG_ARROWS[(direction_limit||'')+(lane_limit||'')] || ''
        html += `
          <svg viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="50" fill="var(--config-fill)" />
            <circle cx="50" cy="50" r="45" fill="white" />
            <path d="${SVG_ARROWS[123]}" fill="none" stroke="#000000aa" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="5,5"/>
            <path d="${arrows}" fill="none" stroke="black" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
            <text x="50" y="57" text-anchor="middle" dominant-baseline="middle" fill="black" stroke="black" stroke-width="1" font-family="Verdana, sans-serif" font-size="32">${speed_limit||''}</text>
            <text x="50" y="82" text-anchor="middle" dominant-baseline="middle" fill="black" stroke="black" stroke-width="1" font-family="Verdana, sans-serif" font-size="20">${hit_tolerance>=0?'+'+hit_tolerance:''}</text>
          </svg>
        `
      }
      elModal.find('.shortcuts').innerHTML = html
      elModal.find('@lane_limit').options = Object.keys(this.lane_zooms)

      if(this.simulator) {
        elModal.setAttribute('simulator', '')
        const elMode = elModal.find('@mode')
        elMode.eventValueChanged = (el, value) => {
          if(value==='simulator') {
            elModal.find('@direction_limit').value = 'approaching'
          }
        }
      }

      // add event listeners to shortcut buttons
      const svgs = elModal.findAll('.shortcuts svg')
      for(let i=0; i<svgs.length; i++) {
        const { direction_limit, lane_limit, speed_limit, hit_tolerance } = this.shortcuts[i]
        svgs[i].addEventListener('click', this.shortcutClicked.bind(this, elModal, direction_limit, lane_limit, speed_limit, hit_tolerance))
      }
    }
    elModal.opening_actor = this

    // update values of input fields using current configuration
    this.updateModalState()
  } // openConfiguration

  shortcutClicked(elModal, direction_limit, lane_limit, speed_limit, hit_tolerance) {
    if(elModal.readonly) return
    if(direction_limit) elModal.updateValues({ direction_limit })
    if(lane_limit) elModal.updateValues({ lane_limit })
    if(speed_limit>0) elModal.updateValues({ speed_limit })
    if(hit_tolerance>=0) elModal.updateValues({ hit_tolerance })
  } // shortcutClicked

  updateModalState(only_state) {
    const elModal = document.body.find('@dopplermeta_status')
    if(!elModal || elModal.opening_actor!==this) return // important to prevent update from different player
    let { state, identity, calibration, optics, limits } = this.meta || {}
    if((state!=='on' && state!=='off') || this.errors?.length>0) state = 'error'
    if(only_state) {
      elModal.updateValues({ state })
    }else {
      const { radar, radar_yaw, camera, lens, camera_yaw } = calibration || {}
      const { zoom } = optics || {}
      let radar_camera = radar ? `${radar} (${radar_yaw}°)` : ''
      if(camera) {
        if(radar_camera) radar_camera += '\n'
        radar_camera += `${camera} + ${lens} (${camera_yaw}° ${zoom}x)`
      }
      const { speed_limit, hit_limit } = limits
      let hit_tolerance = hit_limit - speed_limit
      if(!(hit_tolerance>0)) hit_tolerance = 0
      elModal.updateValues({ ...limits, hit_tolerance, state, radar_camera, ...identity })
    }

    // following handler ensures hit limit is equal or higher than legal speed limit
    elModal.eventValueChanged = (el, value) => {
      if(el.key==='speed_limit' || el.key==='hit_tolerance') {
        const speed_limit = parseInt(elModal.getValue('speed_limit'))
        const hit_tolerance = parseInt(elModal.getValue('hit_tolerance'))
        const hit_limit = elModal.find('@hit_limit')
        hit_limit.value = speed_limit + hit_tolerance
      }
    }

    // handling modal Save button
    elModal.eventSubmit = () => {
      const channel = parseInt(this.parentElement.ch)
      const { mode, direction_limit, lane_limit, speed_limit, hit_limit } = elModal.getValues(true, true, true)
      const zoom = this.lane_zooms[lane_limit] || 0
      if(zoom>0) {
        this.listener.publish('SERVER@msvcCameraAxis', 'mmSetZoom', { channel, zoom })
      }
      const limits = { mode, direction_limit, lane_limit, speed_limit, hit_limit }
      this.listener.publish('SERVER@#DOPPLER', 'mmDopplerSet', { channel, limits })
      elModal.deleteElement()
    }
  } // updateModalState

  updateCalibrationInfo() {
    const { calibration, optics, identity } = this.meta
    const info = this.find('@calibration_info')

    if(!calibration?.calibration_dt) {
      info.classList.remove('available')
      return
    }

    // find or create info element
    const values = { ...calibration, ...optics }
    const { h_pixels, v_pixels, h_angle, v_angle, radar_h_angle, radar_v_angle, camera_yaw, camera_pitch, camera_roll, zoom } = values
    values.camera_lens = values.camera + ' / ' + values.lens
    values.resolution = h_pixels +' x ' + v_pixels
    values.hv_angle = h_angle + ' / ' + v_angle
    values.radar_hv_angle = radar_h_angle + ' / ' + radar_v_angle
    values.camera_yaw_pitch_roll = camera_yaw + ' / ' + camera_pitch + ' / ' + camera_roll
    values.sn = identity?.sn??'-'
    values.zoom = zoom >= 1 ? zoom.toFixed(2) : '?'
    const doppler_version = (identity?.version??'').split('/')
    const doppler_dt = String(doppler_version[5]??'').replace(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/, '$1-$2-$3 $4:$5:$6')
    values.version = (doppler_version[4]||'-') + (doppler_dt ? ` (${doppler_dt})` : '')
    values.checksum = identity?.checksum??'-'
    const client_version = MOSY_APP_WITH_VERSION.split('/')
    const client_dt = String(client_version[5]??'').replace(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/, '$1-$2-$3 $4:$5:$6')
    values.client_version = (client_version[4]||'-') + (client_dt ? ` (${client_dt})` : '')
    values.client_checksum = window.mDopplerClient_md5.replace(/(\w)\w\w\w/g, '$1')
    info.setValues(values)
    info.classList.add('available')
  } // updateCalibrationInfo
} // class meleActorDopplerMeta

meleActorDopplerMeta._properties = PROPERTIES
meleActorDopplerMeta.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-dopplermeta', meleActorDopplerMeta)
